import { downloadFile } from "@/utils";
import { COLORS } from "@/utils/colors";
import { buildFilename } from "@/utils/filename";
import { displayUserName } from "@/utils/userName";

import { createApiClient } from "./apiClient";

export class UserService {
  constructor(authAPIUrl) {
    this.apiClient = createApiClient(authAPIUrl, "v1/users");
  }

  async getUser(user_id) {
    const res = await this.apiClient.get(user_id);
    return mapUser(res);
  }

  async getCurrentUser() {
    const res = await this.apiClient.get("me");
    return mapUser(res);
  }

  async getTeams(id) {
    return this.apiClient.get(`${id}/teams`);
  }

  async getOrgEntities(id) {
    return this.apiClient.get(`${id}/org_entities`);
  }

  async getUsers(offset = 0) {
    const { results, ...pagination } = await this.apiClient.get("", { params: { offset } });
    const data = results?.map(mapUser);
    return { data, ...pagination };
  }

  async createUser(name, email, role) {
    return this.apiClient.post("", { name, email, role });
  }

  /**
   * Updates a user's information
   * @param {string} userId - The ID of the user to update
   *
   * @param {Object} data - The user data to update
   * @param {string} [data.email] - The user's email address
   * @param {string} [data.name] - The user's name
   * @param {string} [data.role] - The user's role
   * @param {string} [data.preferredLanguage] - The user's preferred language
   */
  async updateUser(userId, data) {
    const { preferredLanguage, ...rest } = data;
    if (preferredLanguage) {
      rest.preferred_language = preferredLanguage;
    }

    return this.apiClient.patch(userId, rest);
  }

  /**
   * Updates the properties of the currently logged-in user
   *
   * @param {Object} data - The user data to update
   * @param {string} [data.name] - The user's name
   * @param {string} [data.preferredLanguage] - The user's preferred language
   */
  async updateMe(data) {
    const { preferredLanguage, ...rest } = data;
    if (preferredLanguage) {
      rest.preferred_language = preferredLanguage;
    }

    return this.apiClient.patch("me", rest);
  }

  async deleteUser(id) {
    return this.apiClient.delete(id);
  }

  async search(query, role) {
    return this.apiClient.get("search", { params: { query, role } });
  }

  async changePassword(id, password) {
    return this.apiClient.patch(`${id}/password`, { password }, { timeout: 20000 });
  }

  async resetPassword(userId) {
    return this.apiClient.get(`${userId}/reset-password`, { timeout: 20000 });
  }

  async resetMyPassword() {
    return this.apiClient.get("me/reset-password", { timeout: 20000 });
  }

  async exportUsers(connectionId, connectionName) {
    const res = await this.apiClient.get("export", {
      responseType: "arraybuffer",
      params: connectionId ? { connection_id: connectionId } : undefined,
    });
    downloadFile(buildFilename("users_export", connectionName, "xlsx"), new Blob([res]));
  }
}

function mapUser(serverUser) {
  if (serverUser) {
    const name = displayUserName(serverUser);
    return {
      id: serverUser.id,
      company: serverUser.company,
      role: serverUser.role,
      name,
      email: serverUser.email,
      imageUrl: serverUser.image_url,
      color: serverUser.color || colorFromNumber(hash(name)),
      hash: serverUser.hash,
      preferredLanguage: serverUser.preferred_language,
      permissions: serverUser.permissions,
    };
  }
}

function hash(s) {
  let h = 3496329;
  for (let i = 0; i < s.length; i++) {
    // eslint-disable-next-line no-bitwise
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
}

function colorFromNumber(h) {
  // omit grays and the first shade of every color
  const a = Math.abs(h);
  const hue = a % 6;
  const shade = (Math.floor(a / 6) % 3) + 1;
  return COLORS[hue * 4 + shade];
}
